/**
 * PT-#61 — Bridge para persistir los ejes de la vista EXPORTAR dentro del MISMO saved filter
 * que Publicaciones/Estadísticas/Tendencias (mismo criterio que `trendsFilterBridge`: EXTENDER
 * el modelo existente, NO crear un modelo paralelo de "saved exports").
 *
 * Problema idéntico al de Tendencias: los ejes propios de EXPORTAR (5 dimensiones combinables
 * + alcance temporal) viven como state LOCAL de `ExportPanel`, desacoplado del `SaveFilterModal`
 * (que está en Dashboard y solo ve los filtros de datos de Dashboard). Levantar esos states a
 * Dashboard sería un refactor grande e innecesario para este scope.
 *
 * Solución: un registro singleton a nivel de módulo. El panel registra un snapshot getter +
 * un applier; Dashboard los invoca al guardar/cargar un filtro con scope 'multi' (EXPORTAR y
 * Tendencias comparten el bucket 'multi'). Retrocompatible/undefined-safe: si el panel no está
 * montado o el filtro no trae ejes de export, todo sigue funcionando.
 *
 * Nota de diseño (Trigger C): se mantiene PARALELO a `trendsFilterBridge` en vez de generalizar
 * un bridge único, para no tocar el path de Tendencias ya deployado (v2.22.1). Si aparece una
 * 3ra vista multi-toma con caja propia, ahí conviene generalizar a un bridge con discriminador.
 */

/** Snapshot serializable de los ejes de la vista EXPORTAR que se persisten en el saved filter. */
export interface ExportAxesSnapshot {
  // Dimensiones combinables (definen QUÉ se exporta).
  sites?: string[];
  brands?: string[];
  categories?: string[];
  subcategories?: string[];
  segments?: string[];
  // Alcance temporal (resolución de tomas).
  mode?: 'count' | 'period';
  tomasLimit?: number;
  periodPreset?: string;
  dateFrom?: string; // 'YYYY-MM-DD' (modo Personalizado)
  dateTo?: string;   // 'YYYY-MM-DD' inclusive (modo Personalizado)
}

interface ExportBridgeHandlers {
  /** Devuelve el estado vivo de los ejes de EXPORTAR para guardarlo en el filtro. */
  getSnapshot: () => ExportAxesSnapshot;
  /** Aplica un snapshot (cargado de un saved filter) a la vista EXPORTAR. */
  applySnapshot: (snap: ExportAxesSnapshot) => void;
}

let handlers: ExportBridgeHandlers | null = null;

/** El panel llama esto al montar; devuelve un desregistrador para el cleanup. */
export function registerExportBridge(h: ExportBridgeHandlers): () => void {
  handlers = h;
  return () => {
    if (handlers === h) handlers = null;
  };
}

/**
 * Snapshot de los ejes de EXPORTAR, o null si el panel no está montado.
 * Dashboard lo usa al construir el objeto del saved filter (solo en scope 'multi').
 */
export function getExportSnapshot(): ExportAxesSnapshot | null {
  return handlers ? handlers.getSnapshot() : null;
}

/**
 * Aplica un snapshot a la vista EXPORTAR. No-op seguro si el panel no está montado
 * (ej. se cargó el filtro fuera de la pestaña Exportar) o el filtro es legacy sin ejes de export.
 */
export function applyExportSnapshot(snap: ExportAxesSnapshot | null | undefined): void {
  if (handlers && snap) handlers.applySnapshot(snap);
}
